/**
 * Urban Customz — Business discovery.
 *
 * One file, one IIFE, no dependencies. Everything here is an upgrade on top of
 * markup that already works without it: project cards are real links, need
 * panels are <details>, filters start with nothing hidden, and the booking and
 * quick-view dialogs only exist once JS runs.
 *
 * Modules, in order:
 *   1. ctx       — the shared context (industry / goal / product / project …)
 *   2. track     — analytics, reusing the pipeline in uc-mode.js
 *   3. entry     — homepage entry block stage
 *   4. needs     — desktop tabs over the mobile <details> panels
 *   5. filters   — capability filtering of the project grid
 *   6. search    — client-side structured search over the page's own index
 *   7. quickview — project quick view
 *   8. finder    — guided project finder
 *   9. booking   — Cal.com in a modal
 */
(function () {
  'use strict';
  if (window.__ucDiscovery) return;
  window.__ucDiscovery = true;

  var doc = document;
  var $ = function (sel, root) { return (root || doc).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || doc).querySelectorAll(sel)); };
  /* Escape for insertion into HTML. This must ESCAPE rather than strip:
     stripping quietly ate the ampersand in "Restaurants & Hospitality". */
  var ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) { return ESC_MAP[ch]; });
  };

  /* ======================================================================
     1. Context
     Values are only ever accepted if they match a strict pattern, so nothing
     arbitrary from a URL can reach the page, the quote email or Cal.com.
     ====================================================================== */
  var CTX_PATTERNS = {
    /* Set by the local landing pages (uc-local.js writes the same record). */
    city: /^[a-z0-9-]{1,40}$/,
    industry: /^[a-z0-9-]{1,60}$/,
    goal: /^[a-z0-9-]{1,60}$/,
    capability: /^[a-z0-9-]{1,60}$/,
    product: /^[a-z0-9-]{1,80}$/,
    project: /^[a-z0-9-]{1,80}$/,
    qty: /^[0-9]{1,7}$/,
    when: /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/,
    province: /^[a-z0-9-]{1,40}$/,
    /* Storefront language of the landing page ("en", "fr"); written by uc-local.js. */
    locale: /^[a-z]{2}(-[a-z]{2})?$/,
    timing: /^[a-z0-9-]{1,30}$/,
    region: /^(ca|us|other)$/,
    source: /^[a-z0-9:_.-]{1,60}$/
  };
  var CTX_KEYS = Object.keys(CTX_PATTERNS);
  var CTX_STORE = 'uc_ctx';

  /* Some keys describe the journey and are worth carrying from page to page.
     Others describe only what is on screen right now. `project` is the second
     kind: it may arrive in a URL, because a project quick view is shareable,
     but it must never be stored or stamped onto an outgoing link — otherwise
     every link on the site starts saying "open this project" and the quick
     view reopens itself on whatever page you land on next. */
  var CTX_EPHEMERAL = { project: true };
  var CTX_PERSIST = CTX_KEYS.filter(function (k) { return !CTX_EPHEMERAL[k]; });

  function cleanValue(key, value) {
    if (value == null) return null;
    var v = String(value).trim().toLowerCase();
    var re = CTX_PATTERNS[key];
    return re && re.test(v) ? v : null;
  }

  var ctxState = (function readContext() {
    var out = {};
    try {
      var saved = JSON.parse(window.sessionStorage.getItem(CTX_STORE) || '{}');
      CTX_PERSIST.forEach(function (k) {
        var v = cleanValue(k, saved[k]);
        if (v) out[k] = v;
      });
    } catch (e) { /* blocked storage — context is a convenience, not a requirement */ }
    try {
      var params = new URLSearchParams(window.location.search);
      CTX_KEYS.forEach(function (k) {
        if (!params.has(k)) return;
        var v = cleanValue(k, params.get(k));
        if (v) { out[k] = v; } else { delete out[k]; }
      });
    } catch (e) { /* noop */ }
    return out;
  })();

  function saveContext() {
    var keep = {};
    CTX_PERSIST.forEach(function (k) { if (ctxState[k]) keep[k] = ctxState[k]; });
    try { window.sessionStorage.setItem(CTX_STORE, JSON.stringify(keep)); } catch (e) { /* noop */ }
  }

  var ctx = {
    all: function () {
      var c = {};
      CTX_KEYS.forEach(function (k) { if (ctxState[k]) c[k] = ctxState[k]; });
      return c;
    },
    get: function (k) { return ctxState[k] || null; },
    set: function (patch) {
      Object.keys(patch || {}).forEach(function (k) {
        var v = cleanValue(k, patch[k]);
        if (v) { ctxState[k] = v; } else { delete ctxState[k]; }
      });
      saveContext();
      paintContext();
    },
    drop: function (k) { delete ctxState[k]; saveContext(); paintContext(); },
    /* Append the live context to a URL without clobbering what it already carries. */
    decorate: function (href) {
      if (!href) return href;
      var url;
      try { url = new URL(href, window.location.origin); } catch (e) { return href; }
      CTX_PERSIST.forEach(function (k) {
        if (ctxState[k] && !url.searchParams.has(k)) url.searchParams.set(k, ctxState[k]);
      });
      return url.pathname + (url.search || '') + (url.hash || '');
    },
    label: function (k) {
      var dict = window.UC_LABELS || {};
      var v = ctxState[k];
      if (!v) return null;
      if (k === 'qty') return (window.UC_QTY_LABELS || {})[v] || v + ' pieces';
      if (k === 'when') return v;
      if (k === 'timing') return dict[v] || v.replace(/-/g, ' ');
      /* A city handle is a place name, so title-case it for the summary and
         for the quote email rather than showing "brampton". */
      if (k === 'city') {
        /* Handle words that plain title-casing gets wrong (fort-mcmurray, st-albert,
           sault-ste-marie) and the one handle whose hyphens are part of the name. */
        var cityWhole = {
          'niagara-on-the-lake': 'Niagara-on-the-Lake',
          /* Quebec: accents, and hyphens that are part of the name. */
          'montreal': 'Montr\u00e9al', 'quebec-city': 'Qu\u00e9bec City', 'levis': 'L\u00e9vis', 'saint-jerome': 'Saint-J\u00e9r\u00f4me',
          'chateauguay': 'Ch\u00e2teauguay', 'trois-rivieres': 'Trois-Rivi\u00e8res', 'pointe-claire': 'Pointe-Claire',
          'vaudreuil-dorion': 'Vaudreuil-Dorion', 'rouyn-noranda': 'Rouyn-Noranda',
          'saint-bruno-de-montarville': 'Saint-Bruno-de-Montarville', 'saint-jean-sur-richelieu': 'Saint-Jean-sur-Richelieu',
          'saint-hyacinthe': 'Saint-Hyacinthe', 'salaberry-de-valleyfield': 'Salaberry-de-Valleyfield'
        };
        if (cityWhole[v]) return cityWhole[v];
        var cityFix = { mcmurray: 'McMurray', st: 'St.', ste: 'Ste.' };
        return v.split('-').map(function (w) {
          if (!w) return w;
          return cityFix[w] || (w.charAt(0).toUpperCase() + w.slice(1));
        }).join(' ');
      }
      if (k === 'region') return v === 'us' ? 'United States' : (v === 'ca' ? 'Canada' : 'Elsewhere');
      return dict[v] || v.replace(/-/g, ' ');
    }
  };
  window.UCContext = ctx;

  /* ======================================================================
     2. Analytics — reuse the existing pipeline (GTM dataLayer + Meta + Shopify)
     ====================================================================== */
  function track(name, params) {
    var payload = {};
    var c = ctx.all();
    Object.keys(c).forEach(function (k) { payload['ctx_' + k] = c[k]; });
    Object.keys(params || {}).forEach(function (k) { payload[k] = params[k]; });
    if (typeof window.ucTrack === 'function') { window.ucTrack(name, payload); return; }
    try {
      if (Array.isArray(window.dataLayer)) {
        var ev = { event: name };
        Object.keys(payload).forEach(function (k) { ev[k] = payload[k]; });
        window.dataLayer.push(ev);
      } else if (typeof window.gtag === 'function') {
        window.gtag('event', name, payload);
      }
    } catch (e) { /* analytics must never break the page */ }
    try { if (typeof window.fbq === 'function') window.fbq('trackCustom', name, payload); } catch (e) { /* noop */ }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* noop */ }
  }
  window.UCTrackDiscovery = track;

  /* Links that should carry context declare [data-uc-ctx-link]. The href is
     rewritten on click, so it stays clean in the markup for crawlers. */
  doc.addEventListener('click', function (e) {
    var link = e.target.closest ? e.target.closest('a[data-uc-ctx-link]') : null;
    if (!link || link.getAttribute('data-uc-ctx-done') === '1') return;
    var source = link.getAttribute('data-uc-source');
    if (source) ctx.set({ source: source });
    var extra = link.getAttribute('data-uc-ctx-set');
    if (extra) {
      try { ctx.set(JSON.parse(extra)); } catch (err) { /* ignore malformed */ }
    }
    link.setAttribute('href', ctx.decorate(link.getAttribute('href')));
    link.setAttribute('data-uc-ctx-done', '1');
  }, true);

  /* The context summary shown on the quote page ------------------------- */
  var CTX_FR = (document.documentElement.lang || '').slice(0, 2).toLowerCase() === 'fr';
  var CTX_TITLES = CTX_FR ? {
    city: 'Ville',
    industry: 'Industrie', goal: 'Objectif', product: 'Produit',
    capability: 'Service', project: 'Projet de r\u00e9f\u00e9rence', qty: 'Quantit\u00e9',
    timing: 'D\u00e9lai', region: 'Lieu'
  } : {
    city: 'City',
    industry: 'Industry', goal: 'Goal', product: 'Product',
    capability: 'Service', project: 'Reference project', qty: 'Quantity',
    timing: 'Timing', region: 'Location'
  };
  var CTX_REMOVE = CTX_FR ? 'Retirer ' : 'Remove ';

  function paintContext() {
    var box = $('[data-uc-ctx-box]');
    if (!box) return;
    var list = $('[data-uc-ctx-list]', box);
    var c = ctx.all();
    var shown = Object.keys(CTX_TITLES).filter(function (k) { return c[k]; });
    if (!shown.length) { box.hidden = true; syncQuoteFields({}); return; }
    list.innerHTML = shown.map(function (k) {
      return '<li class="uc-ctx__item"><span>' + CTX_TITLES[k] + '</span><b>' + esc(ctx.label(k)) + '</b>' +
        '<button type="button" class="uc-ctx__drop" data-uc-ctx-drop="' + k +
        '" aria-label="' + CTX_REMOVE + CTX_TITLES[k] + '">&times;</button></li>';
    }).join('');
    box.hidden = false;
    syncQuoteFields(c);
  }

  /* Mirror the context into the existing quote form's hidden fields. */
  function syncQuoteFields(c) {
    var map = {
      city: 'UcQuote-ctx-city',
      industry: 'UcQuote-ctx-industry', goal: 'UcQuote-ctx-goal', product: 'UcQuote-ctx-product',
      capability: 'UcQuote-ctx-capability', project: 'UcQuote-ctx-project', source: 'UcQuote-ctx-source',
      timing: 'UcQuote-ctx-timing', region: 'UcQuote-ctx-region'
    };
    Object.keys(map).forEach(function (k) {
      var el = doc.getElementById(map[k]);
      if (!el) return;
      /* source is a machine value for analytics — never humanise it. */
      el.value = c[k] ? (k === 'source' ? c[k] : (ctx.label(k) || c[k])) : '';
      /* Disabled fields are not submitted, so an empty context adds nothing to
         the email the team receives. */
      el.disabled = !c[k];
    });
    var qty = doc.getElementById('UcQuote-qty');
    if (qty && c.qty && !qty.value) qty.value = c.qty;
    var when = doc.getElementById('UcQuote-date');
    if (when && c.when && !when.value) when.value = c.when;
    var service = $('[data-uc-service]');
    if (service && !service.value) {
      var wanted = (window.UC_SERVICE_MAP || {})[c.goal] || (window.UC_SERVICE_MAP || {})[c.capability];
      if (wanted) {
        var match = Array.prototype.slice.call(service.options).filter(function (o) { return o.value === wanted; })[0];
        if (match) {
          match.selected = true;
          service.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }

  doc.addEventListener('click', function (e) {
    var drop = e.target.closest ? e.target.closest('[data-uc-ctx-drop]') : null;
    if (!drop) return;
    ctx.drop(drop.getAttribute('data-uc-ctx-drop'));
  });

  /* ======================================================================
     2b. Shared dialog controller

     Every .uc-dlg gets its close button, backdrop click, scroll lock and focus
     restore from here. This has to be self-contained: assets/uc-dialog.js — the
     theme's other dialog helper — is only loaded by sections/uc-commission.liquid,
     so on a Business page nothing was listening for [data-uc-dialog-close] and
     the X did nothing. ESC still closed a dialog natively, but not while focus
     sat inside the cross-origin Cal.com iframe, which left the booking modal
     with no way out.

     Running alongside uc-dialog.js is safe: closing an already-closed dialog
     is a no-op, and the scroll lock is only released once nothing is open.

     Cleanup is driven by state rather than by the native `close` event. On this
     storefront a dialog can go from open to closed without `close` ever being
     delivered — verified live: showModal() then close() leaves dialog.open
     false with no close and no cancel event, even for a listener bound straight
     to the element. Anything hung off that event therefore never ran, which is
     what left the page scroll-locked with nothing open and no way out. Every
     path that can close a dialog now calls afterClose(), which is idempotent
     and announces itself as `uc:dialogclosed` for the modules to hook.
     ====================================================================== */
  var dialogOpeners = new WeakMap();
  var openDialogs = [];

  function lockScroll() {
    doc.documentElement.classList.add('uc-dialog-open');
  }

  function releaseScrollIfIdle() {
    var stillOpen = $$('dialog').some(function (d) { return d.open; });
    if (!stillOpen) doc.documentElement.classList.remove('uc-dialog-open');
  }

  /* Runs once per open/close cycle, whichever route closed the dialog. */
  function afterClose(dlg) {
    if (!dlg || dlg.open) return;
    var i = openDialogs.indexOf(dlg);
    if (i === -1) { releaseScrollIfIdle(); return; }
    openDialogs.splice(i, 1);
    releaseScrollIfIdle();
    var opener = dialogOpeners.get(dlg);
    if (opener && doc.contains(opener)) {
      try { opener.focus(); } catch (err) { /* element may have been replaced */ }
    }
    dialogOpeners.delete(dlg);
    try { dlg.dispatchEvent(new CustomEvent('uc:dialogclosed')); } catch (err) { /* noop */ }
  }

  /* Reconcile after anything that could have closed a dialog out of our sight:
     ESC, the browser's own dismissal, a restored back/forward page. */
  function syncDialogs() {
    for (var i = openDialogs.length - 1; i >= 0; i--) {
      if (!openDialogs[i].open) afterClose(openDialogs[i]);
    }
    releaseScrollIfIdle();
  }

  function openDialog(dlg, opener) {
    if (!dlg || typeof dlg.showModal !== 'function') return false;
    if (opener) dialogOpeners.set(dlg, opener);
    if (!dlg.open) {
      try { dlg.showModal(); } catch (e) { return false; }
    }
    if (openDialogs.indexOf(dlg) === -1) openDialogs.push(dlg);
    lockScroll();
    var closeBtn = $('[data-uc-dialog-close]', dlg);
    if (closeBtn) closeBtn.focus();
    return true;
  }

  function closeDialog(dlg) {
    if (!dlg) return;
    if (dlg.open) {
      try { dlg.close(); } catch (e) { dlg.removeAttribute('open'); }
    }
    afterClose(dlg);
  }

  /* What a module hooks instead of `close`. */
  function onDialogClosed(dlg, fn) {
    if (dlg) dlg.addEventListener('uc:dialogclosed', fn);
  }

  doc.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || !target.closest) return;

    var closer = target.closest('.uc-dlg [data-uc-dialog-close]');
    if (closer) {
      e.preventDefault();
      closeDialog(closer.closest('dialog'));
      return;
    }

    /* A click on the backdrop is delivered with the <dialog> itself as target. */
    if (target.classList && target.classList.contains('uc-dlg') && target.open) {
      if (target.hasAttribute('data-uc-keep-open')) return;
      var box = target.getBoundingClientRect();
      var inside = e.clientX >= box.left && e.clientX <= box.right &&
                   e.clientY >= box.top && e.clientY <= box.bottom;
      if (!inside) closeDialog(target);
    }
  });

  /* When the event does arrive it is just one more route into afterClose.
     `close` does not bubble, so listen in the capture phase. */
  doc.addEventListener('close', function (e) {
    var dlg = e.target;
    if (!(dlg instanceof HTMLDialogElement)) return;
    afterClose(dlg);
  }, true);

  /* ESC is handled by the browser and closes the dialog without telling us, so
     reconcile on the next task once the dismissal has happened. */
  doc.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    if (!openDialogs.length) return;
    setTimeout(syncDialogs, 0);
  });

  /* Belt and braces: a stray click, or a back/forward navigation restoring a
     page whose dialog is no longer open, must never leave the page locked. */
  doc.addEventListener('pointerdown', function () {
    if (openDialogs.length) setTimeout(syncDialogs, 0);
  }, true);
  window.addEventListener('pageshow', syncDialogs);

  /* ======================================================================
     3. Homepage entry block — the stage follows the list
     ====================================================================== */
  function initEntry() {
    $$('[data-uc-entry]').forEach(function (root) {
      var rows = $$('[data-uc-entry-row]', root);
      if (!rows.length) return;
      function show(handle) {
        rows.forEach(function (r) { r.classList.toggle('is-on', r.getAttribute('data-uc-entry-row') === handle); });
        $$('[data-uc-entry-shot]', root).forEach(function (s) {
          s.classList.toggle('is-on', s.getAttribute('data-uc-entry-shot') === handle);
        });
        $$('[data-uc-entry-cap]', root).forEach(function (c) {
          c.classList.toggle('is-on', c.getAttribute('data-uc-entry-cap') === handle);
        });
      }
      rows.forEach(function (row) {
        var handle = row.getAttribute('data-uc-entry-row');
        var link = $('a', row);
        ['mouseenter', 'focusin'].forEach(function (ev) {
          row.addEventListener(ev, function () { show(handle); });
        });
        if (link) {
          link.addEventListener('click', function () {
            track('industry_card_clicked', { industry: handle, placement: 'homepage_entry' });
          });
        }
      });
      $$('[data-uc-entry-tile]', root).forEach(function (tile) {
        tile.addEventListener('click', function () {
          track('industry_card_clicked', { industry: tile.getAttribute('data-uc-entry-tile'), placement: 'homepage_entry_mobile' });
        });
      });
      show(rows[0].getAttribute('data-uc-entry-row'));
    });
  }

  /* ======================================================================
     4. Explore by need — desktop tabs drive the same <details> panels
     ====================================================================== */
  function initNeeds() {
    $$('[data-uc-needs]').forEach(function (root) {
      var tabs = $$('[data-uc-need-tab]', root);
      var panels = $$('[data-uc-need-panel]', root);
      if (!panels.length) return;
      var desktop = window.matchMedia('(min-width: 990px)');

      function select(handle, viaUser) {
        tabs.forEach(function (t) {
          t.setAttribute('aria-selected', t.getAttribute('data-uc-need-tab') === handle ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          var on = p.getAttribute('data-uc-need-panel') === handle;
          if (desktop.matches) {
            p.hidden = !on;
            p.open = true;
          } else {
            p.hidden = false;
            if (on && viaUser) p.open = true;
          }
        });
        if (viaUser) {
          track('use_case_opened', { use_case: handle });
          ctx.set({ goal: handle });
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', '#need-' + handle);
          }
        }
      }

      function applyLayout() {
        if (desktop.matches) {
          var current = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
          select(current ? current.getAttribute('data-uc-need-tab') : panels[0].getAttribute('data-uc-need-panel'), false);
        } else {
          panels.forEach(function (p) { p.hidden = false; });
        }
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () { select(tab.getAttribute('data-uc-need-tab'), true); });
      });
      panels.forEach(function (p) {
        p.addEventListener('toggle', function () {
          if (p.open && !desktop.matches) {
            track('use_case_opened', { use_case: p.getAttribute('data-uc-need-panel') });
            ctx.set({ goal: p.getAttribute('data-uc-need-panel') });
          }
        });
      });
      if (desktop.addEventListener) desktop.addEventListener('change', applyLayout);

      var hash = (window.location.hash || '').replace('#need-', '');
      var initial = hash && panels.filter(function (p) { return p.getAttribute('data-uc-need-panel') === hash; })[0];
      applyLayout();
      if (initial) {
        select(hash, false);
        initial.open = true;
        setTimeout(function () { initial.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, 60);
      }
    });
  }

  /* ======================================================================
     5. Capability filters
     ====================================================================== */
  function initFilters() {
    $$('[data-uc-filters]').forEach(function (bar) {
      var gridId = bar.getAttribute('data-uc-filters');
      var grid = doc.getElementById(gridId);
      if (!grid) return;
      var items = $$('[data-uc-caps]', grid);
      var empty = $('[data-uc-filter-empty]', grid.parentNode);
      var active = [];

      function apply() {
        var shown = 0;
        items.forEach(function (item) {
          var caps = (item.getAttribute('data-uc-caps') || '').split(' ').filter(Boolean);
          var ok = !active.length || active.every(function (a) { return caps.indexOf(a) > -1; });
          item.hidden = !ok;
          if (ok) shown++;
        });
        if (empty) empty.hidden = shown > 0;
      }

      $$('[data-uc-filter]', bar).forEach(function (btn) {
        btn.addEventListener('click', function () {
          var key = btn.getAttribute('data-uc-filter');
          if (!key) {
            active = [];
            $$('[data-uc-filter]', bar).forEach(function (b) { b.setAttribute('aria-pressed', b.getAttribute('data-uc-filter') ? 'false' : 'true'); });
          } else {
            var i = active.indexOf(key);
            if (i > -1) { active.splice(i, 1); } else { active.push(key); }
            $$('[data-uc-filter]', bar).forEach(function (b) {
              var k = b.getAttribute('data-uc-filter');
              b.setAttribute('aria-pressed', k ? (active.indexOf(k) > -1 ? 'true' : 'false') : (active.length ? 'false' : 'true'));
            });
            track('discovery_filter_used', { capability: key, active_count: active.length });
          }
          apply();
        });
      });
      apply();
    });
  }

  /* ======================================================================
     6. Search — structured, over the index this page already rendered
     ====================================================================== */
  var TYPE_LABEL = { industry: 'Industries', use_case: 'What you want to do', capability: 'What we make', project: 'Our work' };
  var TYPE_RANK = { industry: 0, use_case: 1, capability: 2, project: 3 };

  function loadIndex() {
    var el = $('[data-uc-index]');
    if (!el) return [];
    try { return JSON.parse(el.textContent) || []; } catch (e) { return []; }
  }

  function tokenize(q) {
    return String(q || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(function (t) { return t.length > 1; });
  }

  /* Keyword lists arrive as arrays. Flattening them once, with sentinel
     newlines, lets an exact keyword match be distinguished from a substring. */
  function keyBlob(item) {
    if (item.__k != null) return item.__k;
    var list = item.k;
    if (!Array.isArray(list)) list = list ? [String(list)] : [];
    var cleaned = list
      .map(function (s) { return String(s).replace(/<[^>]*>/g, '').trim().toLowerCase(); })
      .filter(Boolean);
    item.__k = cleaned.length ? '\n' + cleaned.join('\n') + '\n' : '';
    return item.__k;
  }

  function scoreItem(item, tokens, rawNumber) {
    var name = (item.n || '').toLowerCase();
    var keys = keyBlob(item);
    var summary = (item.s || '').toLowerCase();
    var total = 0;
    tokens.forEach(function (t) {
      var hit = 0;
      if (name === t) hit = 24;
      else if (name.indexOf(t) === 0) hit = 16;
      else if (name.indexOf(t) > -1) hit = 10;
      if (keys.indexOf('\n' + t + '\n') > -1) hit = Math.max(hit, 14);
      else if (keys.indexOf(t) > -1) hit = Math.max(hit, 7);
      if (!hit && summary.indexOf(t) > -1) hit = 3;
      total += hit;
    });
    if (!total) return 0;
    if (rawNumber && item.t === 'project') total += 4;
    return total + (4 - (TYPE_RANK[item.t] || 0));
  }

  function initSearch() {
    var root = $('[data-uc-search]');
    if (!root) return;
    var input = $('[data-uc-search-input]', root);
    var panel = $('[data-uc-search-results]', root);
    var clear = $('[data-uc-search-clear]', root);
    if (!input || !panel) return;
    var index = loadIndex();
    var timer = null;
    var lastQuery = '';

    function close() { panel.hidden = true; panel.innerHTML = ''; }

    function render(q) {
      var tokens = tokenize(q);
      root.classList.toggle('is-filled', q.length > 0);
      if (!tokens.length) { close(); return; }
      var numberMatch = q.match(/\b(\d{2,6})\b/);
      var scored = index
        .map(function (item) { return { item: item, score: scoreItem(item, tokens, !!numberMatch) }; })
        .filter(function (r) { return r.score > 0; })
        .sort(function (a, b) { return b.score - a.score; })
        .slice(0, 12);

      if (!scored.length) {
        panel.innerHTML = '<p class="uc-search__empty">Nothing matched &ldquo;' + esc(q) +
          '&rdquo;. Tell us what you are working on and we will point you at the right thing.</p>' +
          '<a class="uc-search__hit" href="' + ctx.decorate(root.getAttribute('data-uc-quote-url') || '/pages/request-a-quote') +
          '"><span class="uc-search__hit-body"><span class="uc-search__hit-name">Tell us about your project</span>' +
          '<span class="uc-search__hit-meta">We will come back with options and pricing</span></span></a>';
        panel.hidden = false;
        return;
      }

      var groups = {};
      scored.forEach(function (r) { (groups[r.item.t] = groups[r.item.t] || []).push(r.item); });
      var html = Object.keys(groups)
        .sort(function (a, b) { return (TYPE_RANK[a] || 9) - (TYPE_RANK[b] || 9); })
        .map(function (type) {
          return '<div class="uc-search__group"><p class="uc-search__label">' + (TYPE_LABEL[type] || type) + '</p>' +
            groups[type].map(function (item) {
              var thumb = item.img
                ? '<span class="uc-search__hit-thumb"><img src="' + esc(item.img) + '" alt="" loading="lazy" width="40" height="40"></span>'
                : '';
              return '<a class="uc-search__hit" href="' + esc(item.u) + '" data-uc-search-hit="' + esc(item.h) +
                '" data-uc-search-type="' + esc(item.t) + '">' + thumb +
                '<span class="uc-search__hit-body"><span class="uc-search__hit-name">' + esc(item.n) + '</span>' +
                (item.s ? '<span class="uc-search__hit-meta">' + esc(item.s) + '</span>' : '') +
                '</span></a>';
            }).join('') + '</div>';
        }).join('');
      panel.innerHTML = html;
      panel.hidden = false;
      if (q !== lastQuery) {
        lastQuery = q;
        track('discovery_search', { query: q.slice(0, 60), results_count: scored.length });
      }
    }

    input.addEventListener('input', function () {
      window.clearTimeout(timer);
      var q = input.value.trim();
      timer = window.setTimeout(function () { render(q); }, 180);
    });
    input.addEventListener('focus', function () { if (input.value.trim()) render(input.value.trim()); });
    if (clear) {
      clear.addEventListener('click', function () { input.value = ''; root.classList.remove('is-filled'); close(); input.focus(); });
    }
    panel.addEventListener('click', function (e) {
      var hit = e.target.closest ? e.target.closest('[data-uc-search-hit]') : null;
      if (!hit) return;
      track('discovery_search_result_clicked', {
        handle: hit.getAttribute('data-uc-search-hit'),
        result_type: hit.getAttribute('data-uc-search-type'),
        query: input.value.trim().slice(0, 60)
      });
    });
    doc.addEventListener('click', function (e) { if (!root.contains(e.target)) close(); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); input.blur(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
      var hits = $$('[data-uc-search-hit]', panel);
      if (!hits.length) return;
      var i = hits.findIndex(function (h) { return h.classList.contains('is-cursor'); });
      if (e.key === 'Enter') { if (i > -1) { e.preventDefault(); hits[i].click(); } return; }
      e.preventDefault();
      if (i > -1) hits[i].classList.remove('is-cursor');
      var next = e.key === 'ArrowDown' ? (i + 1) % hits.length : (i <= 0 ? hits.length - 1 : i - 1);
      hits[next].classList.add('is-cursor');
      hits[next].scrollIntoView({ block: 'nearest' });
    });
  }

  /* ======================================================================
     7. Project quick view
     ====================================================================== */
  function projectData(card) {
    var holder = $('[data-uc-project]', card);
    if (!holder) return null;
    try { return JSON.parse(holder.textContent); } catch (e) { return null; }
  }

  function renderQuickView(dlg, data) {
    var head = $('[data-uc-qv-eyebrow]', dlg);
    var body = $('[data-uc-qv-body]', dlg);
    var foot = $('[data-uc-qv-foot]', dlg);
    if (head) head.textContent = data.industry_name || 'Our work';

    var gallery = (data.images || []).length
      ? '<div class="uc-qv__gallery" data-uc-qv-gallery>' + data.images.map(function (src, i) {
          return '<div class="uc-qv__shot"><img src="' + esc(src) + '" alt="' +
            esc(data.title + ' — image ' + (i + 1)) + '" loading="' + (i ? 'lazy' : 'eager') + '"></div>';
        }).join('') + '</div>'
      : '';

    var meta = ['client', 'city', 'year', 'quantity']
      .map(function (k) { return data[k]; })
      .filter(Boolean)
      .map(function (v) { return '<span>' + esc(v) + '</span>'; })
      .join('');

    var cols = [];
    if ((data.made || []).length) {
      cols.push('<div class="uc-qv__block"><h4>What we made</h4><ul class="uc-qv__list">' +
        data.made.map(function (m) { return '<li>' + esc(m) + '</li>'; }).join('') + '</ul></div>');
    }
    if ((data.capabilities || []).length) {
      cols.push('<div class="uc-qv__block"><h4>Services used</h4><ul class="uc-qv__list">' +
        data.capabilities.map(function (c) { return '<li>' + esc(c) + '</li>'; }).join('') + '</ul></div>');
    }
    if ((data.methods || []).length) {
      cols.push('<div class="uc-qv__block"><h4>Production</h4><ul class="uc-qv__list">' +
        data.methods.map(function (m) { return '<li>' + esc(m) + '</li>'; }).join('') + '</ul></div>');
    }

    var quote = data.testimonial
      ? '<div class="uc-qv__quote"><p>&ldquo;' + esc(data.testimonial.quote) + '&rdquo;</p><footer>' +
        esc([data.testimonial.name, data.testimonial.role, data.testimonial.company].filter(Boolean).join(', ')) +
        '</footer></div>'
      : '';

    body.innerHTML = gallery +
      '<div class="uc-qv__body">' +
      '<h2 class="uc-h2 uc-qv__title">' + esc(data.title) + '</h2>' +
      (meta ? '<p class="uc-qv__meta">' + meta + '</p>' : '') +
      (data.description ? '<p class="uc-qv__text">' + esc(data.description) + '</p>' : '') +
      (cols.length ? '<div class="uc-qv__cols">' + cols.join('') + '</div>' : '') +
      quote +
      '</div>';

    /* The reference project rides on this one deliberate link rather than on
       the ambient context, so it reaches the quote form and nothing else. */
    var quoteHref = ctx.decorate((dlg.getAttribute('data-uc-quote-url') || '/pages/request-a-quote'));
    try {
      var qu = new URL(quoteHref, window.location.origin);
      qu.searchParams.set('project', data.handle);
      quoteHref = qu.pathname + (qu.search || '') + (qu.hash || '');
    } catch (e) { /* leave the undecorated href */ }

    foot.innerHTML =
      '<a class="uc-btn uc-btn--primary" href="' + esc(quoteHref) + '" data-uc-qv-quote>Start a similar project</a>' +
      '<button type="button" class="uc-btn uc-btn--ghost" data-uc-book data-uc-source="project:' + esc(data.handle) + '">Book a consultation</button>' +
      (data.case_study_url ? '<a class="uc-btn uc-btn--ghost" href="' + esc(data.case_study_url) + '" data-uc-qv-case>Read the full case study</a>' : '');

    var g = $('[data-uc-qv-gallery]', body);
    if (g && (data.images || []).length > 1) {
      var dots = doc.createElement('div');
      dots.className = 'uc-qv__dots';
      dots.innerHTML = data.images.map(function (_, i) { return '<span class="uc-qv__dot' + (i ? '' : ' is-on') + '"></span>'; }).join('');
      g.parentNode.insertBefore(dots, g.nextSibling);
      g.addEventListener('scroll', function () {
        var i = Math.round(g.scrollLeft / g.clientWidth);
        $$('.uc-qv__dot', dots).forEach(function (d, n) { d.classList.toggle('is-on', n === i); });
      }, { passive: true });
    }
  }

  function initQuickView() {
    var dlg = $('[data-uc-qv]');
    if (!dlg || typeof dlg.showModal !== 'function') return;

    /* Project handles as they appear on this page, read once. */
    var cards = null;
    function dataFor(handle) {
      if (!cards) {
        cards = $$('[data-uc-project-card]').map(projectData).filter(Boolean);
      }
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].handle === handle) return cards[i];
      }
      return null;
    }

    /* The only reader of ?project=. Anything that is not a plausible handle,
       or that no card on this page matches, is treated as absent. */
    function wantedFromUrl() {
      var raw;
      try { raw = new URLSearchParams(window.location.search).get('project'); } catch (e) { return null; }
      return cleanValue('project', raw);
    }

    function setParam(handle) {
      if (!window.history || !window.history.replaceState) return;
      var u;
      try { u = new URL(window.location.href); } catch (e) { return; }
      if (handle) { u.searchParams.set('project', handle); } else { u.searchParams.delete('project'); }
      window.history.replaceState(null, '', u.pathname + (u.search || '') + (u.hash || ''));
    }

    /* What `source` was before the quick view claimed it, so closing the quick
       view does not leave every later action attributed to this project. */
    var priorSource = null;

    function open(data, from) {
      if (dlg.open && ctx.get('project') === data.handle) return;
      priorSource = ctx.get('source');
      ctx.set({ project: data.handle, industry: data.industry_handle || ctx.get('industry'), source: 'project:' + data.handle });
      renderQuickView(dlg, data);
      if (!openDialog(dlg, from)) {
        ctx.set({ project: null, source: priorSource });
        return;
      }
      track('project_opened', { project: data.handle, industry: data.industry_handle || '' });
      setParam(data.handle);
    }

    doc.addEventListener('click', function (e) {
      var card = e.target.closest ? e.target.closest('[data-uc-project-card]') : null;
      if (!card) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      var data = projectData(card);
      if (!data) return;
      e.preventDefault();
      open(data, e.target.closest('a'));
    });

    dlg.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-uc-qv-quote]')) {
        track('request_quote_clicked', { source: ctx.get('source') || 'project', placement: 'project_quickview' });
      }
      if (e.target.closest && e.target.closest('[data-uc-qv-case]')) {
        track('project_case_study_viewed', { project: ctx.get('project') || '', placement: 'project_quickview' });
      }
    });

    /* Scroll lock and focus restore are handled by the shared controller. This
       clears the state that would otherwise make the quick view come back:
       the context entry, the source it borrowed, and the URL parameter. */
    onDialogClosed(dlg, function () {
      ctx.set({ project: null, source: priorSource });
      priorSource = null;
      setParam(null);
    });

    /* Deep link: ?project=<handle> in the URL opens the quick view on load.
       Read from the URL, never from stored context — a project looked at
       earlier in the session must not reopen itself on the next page. A handle
       that matches nothing here is stripped rather than left in the address
       bar to be shared or bookmarked. */
    var wanted = wantedFromUrl();
    if (wanted) {
      var first = dataFor(wanted);
      if (first) { open(first, null); } else { ctx.set({ project: null }); setParam(null); }
    }

    /* Back and forward move the quick view with the URL instead of leaving the
       two out of step. */
    window.addEventListener('popstate', function () {
      var now = wantedFromUrl();
      if (!now) {
        if (dlg.open) closeDialog(dlg);
        return;
      }
      var d = dataFor(now);
      if (d) { open(d, null); } else if (dlg.open) { closeDialog(dlg); }
    });
  }

  /* ======================================================================
     8. Project finder
     ====================================================================== */
  function initFinder() {
    var dlg = $('[data-uc-finder]');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    var steps = $$('[data-uc-find-step]', dlg);
    var bar = $('[data-uc-find-bar]', dlg);
    var back = $('[data-uc-find-back]', dlg);
    var result = $('[data-uc-find-result]', dlg);
    var answers = {};
    var at = 0;

    function paint() {
      steps.forEach(function (s, i) { s.hidden = i !== at; });
      if (bar) bar.style.width = Math.round(((at) / steps.length) * 100) + '%';
      if (back) back.hidden = at === 0;
      var focusable = steps[at] && $('button, input, select', steps[at]);
      if (focusable) focusable.focus({ preventScroll: true });
    }

    function finish() {
      ctx.set({
        industry: answers.industry, goal: answers.goal, capability: answers.capability,
        qty: answers.qty, timing: answers.timing, region: answers.region, source: 'finder'
      });
      if (bar) bar.style.width = '100%';
      steps.forEach(function (s) { s.hidden = true; });
      renderResult();
      result.hidden = false;
      track('finder_completed', answers);
    }

    function renderResult() {
      var index = loadIndex();
      var want = [answers.industry, answers.goal, answers.capability].filter(Boolean);
      var projects = index
        .filter(function (i) { return i.t === 'project'; })
        .map(function (i) {
          var tags = (i.tags || '').split(' ');
          return { item: i, hits: want.filter(function (w) { return tags.indexOf(w) > -1; }).length };
        })
        .sort(function (a, b) { return b.hits - a.hits; })
        .slice(0, 4)
        .filter(function (r) { return r.hits > 0; })
        .map(function (r) { return r.item; });

      var caps = index.filter(function (i) {
        return i.t === 'capability' && (!answers.capability || i.h === answers.capability);
      }).slice(0, 6);

      var summary = ['industry', 'goal', 'capability', 'qty', 'timing', 'region']
        .filter(function (k) { return answers[k]; })
        .map(function (k) {
          var label;
          if (k === 'qty') label = (window.UC_QTY_LABELS || {})[answers[k]] || answers[k] + ' pieces';
          else if (k === 'region') label = answers[k] === 'us' ? 'United States' : (answers[k] === 'ca' ? 'Canada' : 'Elsewhere');
          else label = (window.UC_LABELS || {})[answers[k]] || String(answers[k]).replace(/-/g, ' ');
          return '<dt>' + (CTX_TITLES[k] || k) + '</dt><dd>' + esc(label) + '</dd>';
        }).join('');

      var quoteHref = ctx.decorate(dlg.getAttribute('data-uc-quote-url') || '/pages/request-a-quote');
      $('[data-uc-find-summary]', result).innerHTML = summary ? '<dl>' + summary + '</dl>' : '';
      $('[data-uc-find-matches]', result).innerHTML =
        (caps.length
          ? '<div class="uc-qv__block"><h4>What we would use</h4><ul class="uc-qv__list">' +
            caps.map(function (c) { return '<li>' + esc(c.n) + '</li>'; }).join('') + '</ul></div>'
          : '') +
        (projects.length
          ? '<div class="uc-qv__block"><h4>Work like yours</h4><ul class="uc-qv__list">' +
            projects.map(function (p) { return '<li><a href="' + esc(p.u) + '">' + esc(p.n) + '</a></li>'; }).join('') + '</ul></div>'
          : '');
      $('[data-uc-find-actions]', result).innerHTML =
        '<a class="uc-btn uc-btn--primary" href="' + esc(quoteHref) + '" data-uc-find-quote>Get a quote</a>' +
        '<button type="button" class="uc-btn uc-btn--ghost" data-uc-book data-uc-source="finder">Book a consultation</button>';
    }

    dlg.addEventListener('click', function (e) {
      var opt = e.target.closest ? e.target.closest('[data-uc-find-opt]') : null;
      if (opt) {
        var key = opt.getAttribute('data-uc-find-key');
        var value = opt.getAttribute('data-uc-find-opt');
        answers[key] = value;
        $$('[data-uc-find-key="' + key + '"]', dlg).forEach(function (b) {
          b.setAttribute('aria-pressed', b === opt ? 'true' : 'false');
        });
        track('finder_step_completed', { step: at + 1, key: key, value: value });
        at++;
        if (at >= steps.length) { finish(); } else { paint(); }
        return;
      }
      if (e.target.closest && e.target.closest('[data-uc-find-skip]')) {
        at++;
        if (at >= steps.length) { finish(); } else { paint(); }
        return;
      }
      if (e.target.closest && e.target.closest('[data-uc-find-back]')) {
        at = Math.max(0, at - 1);
        result.hidden = true;
        paint();
        return;
      }
      if (e.target.closest && e.target.closest('[data-uc-find-quote]')) {
        track('request_quote_clicked', { source: 'finder', placement: 'project_finder' });
      }
    });

    dlg.addEventListener('change', function (e) {
      var free = e.target.closest ? e.target.closest('[data-uc-find-free]') : null;
      if (!free) return;
      answers[free.getAttribute('data-uc-find-key')] = free.value.trim().toLowerCase();
    });

    doc.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-uc-find-open]') : null;
      if (!trigger) return;
      e.preventDefault();
      answers = {};
      at = 0;
      result.hidden = true;
      paint();
      if (!openDialog(dlg, trigger)) return;
      track('finder_started', { placement: trigger.getAttribute('data-uc-source') || '' });
    });

    /* Scroll lock and focus restore are handled by the shared controller. */
    onDialogClosed(dlg, function () {
      if (at < steps.length && result.hidden) track('finder_abandoned', { step: at + 1 });
    });
  }

  /* ======================================================================
     9. Booking — Cal.com in a modal.
     Same account and the same iframe approach the Swag Platform already uses,
     so there is one booking system, not two. The iframe is only created when
     the dialog opens, so Cal.com is never loaded on a page view.
     ====================================================================== */
  function initBooking() {
    var dlg = $('[data-uc-booking]');
    if (!dlg) return;
    var mount = $('[data-uc-booking-mount]', dlg);
    var done = $('[data-uc-booking-done]', dlg);
    var base = dlg.getAttribute('data-uc-cal-url') || '';
    var theme = dlg.getAttribute('data-uc-cal-theme') || 'light';
    var booked = false;

    function buildUrl() {
      var url;
      try { url = new URL(base); } catch (e) { return null; }
      var c = ctx.all();
      var notes = [];
      if (c.industry) notes.push('Industry: ' + (ctx.label('industry') || c.industry));
      if (c.goal) notes.push('Goal: ' + (ctx.label('goal') || c.goal));
      if (c.capability) notes.push('Service: ' + (ctx.label('capability') || c.capability));
      if (c.product) notes.push('Product: ' + (ctx.label('product') || c.product));
      if (c.project) notes.push('Reference project: ' + (ctx.label('project') || c.project));
      if (c.qty) notes.push('Approx quantity: ' + (ctx.label('qty') || c.qty));
      if (c.timing) notes.push('Timing: ' + (ctx.label('timing') || c.timing));
      if (c.when) notes.push('Needed by: ' + c.when);
      if (c.region) notes.push('Location: ' + (ctx.label('region') || c.region));
      notes.push('From: ' + window.location.pathname);
      url.searchParams.set('notes', notes.join('\n'));
      /* Individual params land in matching Cal.com booking questions when they
         exist, and are ignored when they do not. */
      ['industry', 'goal', 'capability', 'product', 'project', 'source'].forEach(function (k) {
        if (c[k]) url.searchParams.set(k, c[k]);
      });
      var customer = window.UC_CUSTOMER || {};
      if (customer.name) url.searchParams.set('name', customer.name);
      if (customer.email) url.searchParams.set('email', customer.email);
      url.searchParams.set('embed', 'true');
      url.searchParams.set('theme', theme);
      return url.toString();
    }

    function open(from) {
      var src = buildUrl();
      booked = false;
      if (done) done.hidden = true;
      if (!src) {
        mount.innerHTML = '<p class="uc-book__fallback">Booking is unavailable right now. Call ' +
          esc(dlg.getAttribute('data-uc-phone') || '') + ' or send us a quote request and we will arrange a time.</p>';
      } else {
        mount.innerHTML = '<div class="uc-book__loading"><span class="uc-book__spinner" aria-hidden="true"></span>' +
          '<span>Loading available times&hellip;</span></div>';
        var frame = doc.createElement('iframe');
        frame.className = 'uc-book__frame';
        frame.title = 'Book a consultation with Urban Customz';
        frame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups');
        frame.setAttribute('loading', 'lazy');
        frame.src = src;
        frame.addEventListener('load', function () {
          var loading = $('.uc-book__loading', mount);
          if (loading) loading.remove();
        });
        mount.appendChild(frame);
      }
      if (!openDialog(dlg, from)) return;
      track('book_consultation_clicked', { source: ctx.get('source') || '', placement: from ? (from.getAttribute('data-uc-source') || '') : '' });
    }

    doc.addEventListener('click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-uc-book]') : null;
      if (!trigger) return;
      e.preventDefault();
      var src = trigger.getAttribute('data-uc-source');
      if (src) ctx.set({ source: src });
      open(trigger);
    });

    window.addEventListener('message', function (e) {
      var origin;
      try { origin = new URL(base).origin; } catch (err) { return; }
      if (e.origin !== origin) return;
      var data = e.data;
      if (!data || data.action !== 'bookingSuccessful' || booked) return;
      booked = true;
      var booking = (data.payload && data.payload.booking) || {};
      track('consultation_booked', { booking_uid: booking.uid || '', start_time: booking.startTime || '' });
      if (done) {
        mount.innerHTML = '';
        done.hidden = false;
      }
    });

    /* Scroll lock and focus restore are handled by the shared controller.
       Emptying the mount tears down the Cal.com iframe so it stops running. */
    onDialogClosed(dlg, function () {
      mount.innerHTML = '';
    });
  }

  /* ======================================================================
     Boot
     ====================================================================== */

  /* ======================================================================
     Quote outcome — the one conversion this form produces.

     Shopify's contact form posts to /contact and, on success, re-renders this
     page with the success panel (form.posted_successfully?), which the section
     marks with [data-uc-quote-success]. That re-render is the ONLY moment a
     quote has actually been received, so that is the only moment the event
     fires — never on opening or starting the form.

     What travels with it is the journey, not the person: the local landing
     page it came from (city / province / page type, from the shared context),
     the service chosen and a quantity band, both stashed at submit time
     because the re-rendered page no longer holds the posted values. Names,
     emails, phones, files and the message never leave the form.
     ====================================================================== */
  function quoteOutcome() {
    var META = 'uc_quote_meta';
    var FIRED = 'uc_quote_fired';
    var form = doc.getElementById('UcQuoteForm');

    function band(n) {
      var q = parseInt(n, 10);
      if (!isFinite(q) || q <= 0) return '';
      if (q < 12) return '1-11';
      if (q < 50) return '12-49';
      if (q < 150) return '50-149';
      if (q < 500) return '150-499';
      return '500+';
    }

    if (form) {
      form.addEventListener('submit', function () {
        try {
          var service = form.querySelector('[data-uc-service]');
          var qty = doc.getElementById('UcQuote-qty');
          var when = doc.getElementById('UcQuote-date');
          var meta = {
            t: Date.now(),
            quote_type: service && service.value ? String((service.options && service.options[service.selectedIndex] && service.options[service.selectedIndex].getAttribute('data-uc-key')) || service.value).slice(0, 60) : '',
            quantity_band: band(qty && qty.value),
            needed_by_set: when && when.value ? 'yes' : 'no'
          };
          window.sessionStorage.setItem(META, JSON.stringify(meta));
        } catch (e) { /* storage blocked: the event still fires without the extras */ }
      });
    }

    var success = $('[data-uc-quote-success]');
    if (!success) return;
    var meta = {};
    try {
      meta = JSON.parse(window.sessionStorage.getItem(META) || '{}');
      /* Refreshing the success page must not count a second quote. */
      if (meta.t && window.sessionStorage.getItem(FIRED) === String(meta.t)) return;
      if (meta.t) window.sessionStorage.setItem(FIRED, String(meta.t));
      window.sessionStorage.removeItem(META);
    } catch (e) { /* noop */ }

    var c = ctx.all();
    var m = /^local:([a-z0-9-]+):(apparel|printing|wallart)$/.exec(c.source || '');
    var paths = { apparel: 'custom-t-shirts', printing: 'custom-printing', wallart: 'custom-wall-art' };
    var params = {
      quote_type: meta.quote_type || '',
      quantity_band: meta.quantity_band || '',
      needed_by_set: meta.needed_by_set || '',
      lead_source: c.source || ''
    };
    if (c.city) params.city = c.city;
    if (c.province) params.province = c.province;
    /* The language of the landing page the journey started on, not of this form. */
    if (c.locale) params.locale = c.locale;
    if (m) {
      params.page_type = m[2];
      params.landing_page = '/pages/' + paths[m[2]] + '/' + m[1];
    }
    track('quote_submitted', params);
  }

  function boot() {
    quoteOutcome();
    paintContext();
    initEntry();
    initNeeds();
    initFilters();
    initSearch();
    initQuickView();
    initFinder();
    initBooking();
  }

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
